import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { makeStyles, Paper, Typography } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
	flexboxWrapper: {
		display: "flex",
		overflow: "auto",
		flexWrap: "wrap-reverse",
		[theme.breakpoints.up("md")]: {
			flexWrap: "nowrap",
		},
		width: "100%",
		paddingRight: "1rem",
	},
	regular: {
		[theme.breakpoints.up("md")]: {
			marginRight: "1rem",
			maxHeight: "calc(100vh - 7rem)",
		},
		"& a": {
			color: theme.palette.primary.main + "!important",
		},
		"& h2": {
			color: theme.palette.secondary.main,
			paddingTop: "5rem",
			marginTop: "-3rem",
		},
		"& h3": {
			paddingTop: "5rem",
			marginTop: "-4rem",
		},
	},
	empty: {
		height: "100vh",
	},
	sticky: {
		padding: "1rem",
		marginBottom: "1rem",
		minWidth: "20%",
		maxHeight: "85vh",
		overflowY: "auto",
		[theme.breakpoints.up("md")]: {
			position: "sticky",
			top: 0,
			alignSelf: "flex-start",
		},
		"& ul": {
			listStyleType: "none",
			paddingLeft: 0,
			"& a": {
				color: theme.palette.primary.main,
				textDecoration: "none",
			},
			"& li": {
				marginBottom: ".5rem",
				fontWeight: "bold",
			},
		},
	},
	secondaryUl: {
		paddingLeft: "1rem!important",
		"& li": {
			fontWeight: "normal!important",
		},
	},
	active: {
		color: theme.palette.secondary.main,
		"& a": {
			color: theme.palette.secondary.main + "!important",
		},
	},
}));

const useIntersectionObserver = (setActiveId) => {
	const headingElementsRef = useRef({});
	useEffect(() => {
		const callback = (headings) => {
			headingElementsRef.current = headings.reduce((map, headingElement) => {
				map[headingElement.target.id] = headingElement;
				return map;
			}, headingElementsRef.current);
			const visibleHeadings = [];
			Object.keys(headingElementsRef.current).forEach((key) => {
				const headingElement = headingElementsRef.current[key];
				if (headingElement.isIntersecting) visibleHeadings.push(headingElement);
			});

			const getIndexFromId = (id) =>
				headingElements.findIndex((heading) => heading.id === id);
			if (visibleHeadings.length === 1) {
				setActiveId(visibleHeadings[0].target.id);
			} else if (visibleHeadings.length > 1) {
				const sortedVisibleHeadings = visibleHeadings.sort(
					(a, b) => getIndexFromId(a.target.id) > getIndexFromId(b.target.id)
				);
				setActiveId(sortedVisibleHeadings[0].target.id);
			}
		};
		const observer = new IntersectionObserver(callback, {
			rootMargin: "-110px 0px -40% 0px",
		});

		const headingElements = Array.from(document.querySelectorAll("h2, h3"));
		headingElements.forEach((element) => observer.observe(element));

		return () => observer.disconnect();
	}, [setActiveId]);
};

const getNestedHeadings = (headingElements) => {
	const nestedHeadings = [];

	headingElements.forEach((heading, index) => {
		const { innerText: title, id } = heading;

		if (heading.nodeName === "H2") {
			nestedHeadings.push({ id, title, items: [] });
		} else if (heading.nodeName === "H3" && nestedHeadings.length > 0) {
			nestedHeadings[nestedHeadings.length - 1].items.push({
				id,
				title,
			});
		}
	});

	return nestedHeadings;
};

const useHeadingsData = () => {
	const [nestedHeadings, setNestedHeadings] = useState([]);

	useEffect(() => {
		const headingElements = Array.from(document.querySelectorAll("h2, h3"));

		const newNestedHeadings = getNestedHeadings(headingElements);
		setNestedHeadings(newNestedHeadings);
	}, []);

	return { nestedHeadings };
};

const Headings = ({ headings, activeId }) => {
	const classes = useStyles();
	return (
		<ul>
			{headings.map((heading) => (
				<li
					key={heading.id}
					className={heading.id === activeId ? classes.active : ""}
				>
					<a
						href={`#${heading.id}`}
						onClick={(e) => {
							e.preventDefault();
							document.querySelector(`#${heading.id}`).scrollIntoView({
								behavior: "smooth",
							});
						}}
					>
						{heading.title}
					</a>
					{heading.items.length > 0 && (
						<ul className={classes.secondaryUl}>
							{heading.items.map((child) => (
								<li
									key={child.id}
									className={child.id === activeId ? classes.active : ""}
								>
									<a
										href={`#${child.id}`}
										onClick={(e) => {
											e.preventDefault();
											document.querySelector(`#${child.id}`).scrollIntoView({
												behavior: "smooth",
											});
										}}
									>
										{child.title}
									</a>
								</li>
							))}
						</ul>
					)}
				</li>
			))}
		</ul>
	);
};

export default function Documentation(props) {
	const classes = useStyles();
	const [activeId, setActiveId] = useState();
	const { nestedHeadings } = useHeadingsData();
	useIntersectionObserver(setActiveId);

	return (
		<>
			<div className={classes.flexboxWrapper}>
				<div className={classes.regular}>
					<Typography variant="h4" component="h1">
						Documentation
					</Typography>

					<h2 id="about">About</h2>
					<p style={{ fontWeight: "bold" }}>
						Welcome to the Online Classroom application dashboard!
					</p>
					<p>
						This application was developed as a replacement for the Shaping the
						constitution and Union or Secession applications. The originals had
						to be scrapped due to very old software versions that had some
						serious security vulnerabilities. The new version of this
						application includes a full content management system (CMS).
					</p>

					<h2 id="getting-started">Getting Started</h2>
					<ul>
						<li>
							Files can be shared between sites and can be added at any time on
							the{" "}
							<Link to={`/${process.env.REACT_APP_ADMIN_URL}/files/add`}>
								Add Files
							</Link>{" "}
							page.
						</li>
						<li>
							Start by creating a new Site on the{" "}
							<Link to={`/${process.env.REACT_APP_ADMIN_URL}/sites/add`}>
								Add Site
							</Link>{" "}
							page. A "Site" must be created before anything else you want
							within them, such as Units, Themes (themes work similarly to
							"tags" or "categories" in other Content Management Systems),
							Entries, and People.
						</li>
						<li>
							After a Site is created, you can then{" "}
							<Link to={`/${process.env.REACT_APP_ADMIN_URL}/units/add`}>
								add Units
							</Link>{" "}
							and{" "}
							<Link to={`/${process.env.REACT_APP_ADMIN_URL}/themes/add`}>
								add Themes
							</Link>{" "}
							(themes work similarly to "tags" or "categories" in other Content
							Management Systems) to the Site
						</li>
						<li>
							Once Units are created, you can{" "}
							<Link to={`/${process.env.REACT_APP_ADMIN_URL}/people/add`}>
								add People
							</Link>{" "}
							and{" "}
							<Link to={`/${process.env.REACT_APP_ADMIN_URL}/entries/add`}>
								add Entries
							</Link>{" "}
							to those Units
						</li>
						<li>
							After all resources are created for your site,{" "}
							<Link to={`/${process.env.REACT_APP_ADMIN_URL}/menus`}>
								view the Menus page
							</Link>{" "}
							(alternatively click the three dots next to the Sites link in the
							sidebar to the left), and click the pencil next to the name of the
							site. This will allow you to create/modify the navigation links at
							the top of the site. For more information on how to edit menus,{" "}
							<a href="#menus">please click here.</a>
						</li>
					</ul>
					<h2 id="site-architecture">Site architecture</h2>
					<p>The sites can be broken down by the following diagram:</p>
					<pre
						style={{
							fontName: "mono",
							fontSize: ".75rem",
						}}
					>{`
                                      ┌──────────┐
                                      │          │
      ┌───────────────────────────────┤   Site   │
      │                               │          │
      │                               └─────┬────┘
      │                                     │
      │                                     │ One to Many
      │                                     │
      │                               ┌─────▼────┐
      │One to Many                    │          │
      │                               │   Unit   │
      │                               │          │
      │                               └─────┬────┘
      │                                     │
      │                                     │ One to Many
      │                                     │
      │                                     │
      │                    ┌──────────┐     │    ┌──────────┐
      │                    │          │     │    │          │
      │  ┌─────────────────►   Entry  ◄─────┴────►  Person  │
      │  │   Many to Many  │          │          │          │
      │  │                 └──────────┘          └─────▲────┘
      │  │                                             │
      │  │                                             │
      │  │                                             │
┌─────▼──▼─┐                                           │
│          │               Many to Many                │
│   Theme  ◄───────────────────────────────────────────┘
│          │
└──────────┘	
					`}</pre>

					<h2 id="files">Files</h2>
					<p>This section contains the information relevant to Files</p>
					<h3 id="add-files">Uploading Files</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/files/add`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Add Files
						</Link>{" "}
						page to upload your files. Either click and drag them to the drop
						zone, or click the drop zone to browse for files. You can select
						multiple files in the file browser with <b>SHIFT + LEFT CLICK</b> or{" "}
						<b>CTRL + LEFT CLICK</b>.
					</p>
					<h3 id="view-files">View Files</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/files`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Files
						</Link>{" "}
						page to view your uploaded files. Clicking the "File Name" of the
						file will cpy the file's URL to your clipboard.
					</p>

					<h2 id="external-sites">External Sites</h2>
					<p>
						External Site Links for the Site Select page can be added on{" "}
						<Link to={`/${process.env.REACT_APP_ADMIN_URL}/externalsites/add`}>
							this page
						</Link>
						. This can also be found by clicking the three dots next to the
						"Sites" link in the sidebar to the left.
					</p>
					<h2 id="sites">Sites</h2>
					<p>This section contains the information relevant to Sites</p>
					<h3 id="create-a-site">Add a Site</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/sites/add`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Add a Site
						</Link>{" "}
						page to create a new site. Fill out all of the fields on the page.
						On the right side of the add site page, there is a toggle for
						publishing the site if you would like the public to see it on the
						public facing site. You may also set a featured image on the right
						side before saving. The "Warning" section is optional, and will
						display on the homepage of the site as well as each of the singular
						entry/person pages.
					</p>
					<h3 id="view-sites">View Sites</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/sites/add`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Sites
						</Link>{" "}
						page to view all of the sites.
					</p>
					<h3 id="edit-a-site">Edit a Site</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/sites`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Sites
						</Link>{" "}
						page and click the pencil next to the site you would like to edit.
						When finished editing, click the "SAVE" button to save your changes.
					</p>
					<h3 id="delete-sites">Delete Sites</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/sites`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Sites
						</Link>{" "}
						page and click the checkbox next to the site(s) you would like to
						delete. Click the trash can icon at the top of the page to remove
						the selected sites.
					</p>

					<h2 id="menus">Menus</h2>
					<p>
						This section contains the information relevant to Menus. Each site
						has a menu created by default when a site is created, so there is no
						need to create a new menu manually.
					</p>
					<h3 id="view-menus">View Menus</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/menus`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Menus
						</Link>{" "}
						page to view all of the menus. You can navigate to this page by clicking the three dots next to the Sites link in the
							sidebar to the left, and then clicking the pencil next to the name of the
							site. This will allow you to create/modify the navigation links at
							the top of the site. 
					</p>
					<h3 id="edit-menus">Edit Menus</h3>
					<p>
						Menus can be edited by visiting the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/menus`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Menus
						</Link>{" "}
						page and clicking the pencil icon to the left of the name of the
						Site.
					</p>
					<p>
						You can drag-and-drop the main links and dropdown links as needed.
					</p>
					<p>
						Click the large <b>+</b> symbol at the very bottom of the menu to
						add new main links. To edit a main link, click the pencil next to he
						url of the main link. To delete it, click the trash can. The smaller{" "}
						<b>+</b> symbol on the right is how you can add a dropdown link to
						that main menu section. Inside each of the dropdown links, you can
						edit them with the pencil icon, or delete them with the trash can
						icon.
					</p>
					<p>
						When finished with your changes, please scroll all the way to the
						bottom and click the <b>SAVE</b> button
					</p>

					<h2 id="pages">Pages</h2>
					<p>
						<b>Pages</b> are essentially custom pages for general use. These can
						be used for something like an "About" page. Make sure to add the
						page to your site's menu when you are finished.
					</p>
					<h3 id="add-page">Add a Page</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/pages/add`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Add Page
						</Link>{" "}
						page and fill out all of the fields to create your page.
					</p>
					<h3 id="view-pages">View Pages</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/pages`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Pages
						</Link>{" "}
						page to view all of the pages.
					</p>
					<h3 id="edit-a-page">Edit a Page</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/pages`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Pages
						</Link>{" "}
						page and click the pencil next to the page you would like to edit.
						When finished editing, click the "SAVE" button to save your changes.
					</p>
					<h3 id="delete-pages">Delete Pages</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/pages`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Pages
						</Link>{" "}
						page and click the checkbox next to the page(s) you would like to
						delete. Click the trash can icon at the top of the page to remove
						the selected pages.
					</p>

					<h2 id="units">Units</h2>
					<p>This section contains the information relevant to Units</p>
					<h3 id="create-a-unit">Add a Unit</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/units/add`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Add a Unit
						</Link>{" "}
						page to create a new unit. Fill out all of the fields on the page.
					</p>
					<h3 id="view-units">View Units</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/units`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Units
						</Link>{" "}
						page to view all of the units. Fill out all of the fields on the
						page.
					</p>
					<h3 id="edit-units">Edit Units</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/units`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Units
						</Link>{" "}
						page to view all of the units and click the pencil icon next to the
						unit you would like to edit. Make the changes you would like and
						click the "SAVE" button.
					</p>
					<h3 id="delete-units">Delete Units</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/units`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Units
						</Link>{" "}
						page to view all of the units. Select the checkbox of the unit(s)
						you would like to remove, and then click the trash can icon.
						<br />
						<br />
						<span style={{ color: "red", fontWeight: "bold" }}>
							Warning:
						</span>{" "}
						Deleting a unit will delete all of the entries within that unit. If
						needed, please reassign the entries to another unit before removing.
					</p>

					<h2 id="people">People</h2>
					<p>
						This section contains the information relevant to People. People can
						be shared between sites and can have a one to many relationship with
						Units
					</p>
					<h3 id="create-a-person">Add a Person</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/people/add`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Add a Person
						</Link>{" "}
						page to create a new person. Fill out all of the fields on the page.
						On the right side of the page, there is a toggle for publishing the
						person if you would like the public to see it on the public facing
						site. You may also set a featured image on the right side before
						saving.
					</p>
					<h3 id="view-people">View People</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/people`}
							target="_blank"
							rel="noopener noreferrer"
						>
							People
						</Link>{" "}
						page to view all of the people.
					</p>
					<h3 id="edit-people">Edit People</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/people`}
							target="_blank"
							rel="noopener noreferrer"
						>
							People
						</Link>{" "}
						page to view all of the people. Select the pencil icon next to the
						person you would like to edit.
					</p>
					<h3 id="delete-people">Delete People</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/people`}
							target="_blank"
							rel="noopener noreferrer"
						>
							People
						</Link>{" "}
						page to view all of the people. Select the checkboxes of the people
						you would like to remove and click the trash can icon at the top of
						the page.
					</p>

					<h2 id="entries">Entries</h2>
					<p>This section contains the information relevant to Entries</p>
					<h3 id="add-entry">Add an Entry</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/entries/add`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Add an Entry
						</Link>{" "}
						page to create a new entry. Fill out all of the fields on the page.
						On the right side of the page, there is a toggle for publishing the
						entry if you would like the public to see it on the public facing
						site. You may also set a featured image, transcription, and hires
						pdf/image on the right side before saving.
					</p>
					<h3 id="view-entries">View Entries</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/entries`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Entries
						</Link>{" "}
						page to view all of the entries.
					</p>
					<h3 id="edit-entries">Edit Entries</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/entries`}
							target="_blank"
							rel="noopener noreferrer"
						>
							People
						</Link>{" "}
						page to view all of the entries. Select the pencil icon next to the
						entry you would like to edit. Click the "SAVE" button when finished
						editing.
					</p>
					<h3 id="delete-entries">Delete Entries</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/entries`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Entries
						</Link>{" "}
						page to view all of the entries. Select the checkboxes of the
						entries you would like to remove and click the trash can icon at the
						top of the page.
					</p>

					<h2 id="themes">Themes</h2>
					<p>This section contains information related to themes.</p>
					<h3 id="add-theme">Add a Theme</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/themes/add`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Add a Theme
						</Link>{" "}
						page to add a new theme for a site. Fill out the form and click the
						"SAVE" button when finished.
					</p>
					<h3 id="view-themes">View Themes</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/themes`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Themes
						</Link>{" "}
						page to view all of the themes.
					</p>
					<h3 id="edit-themes">Edit a Theme</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/themes`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Themes
						</Link>{" "}
						page to view all of the themes. Click the pencil icon next to the
						theme you would like to update. When finished with your changes,
						click the "SAVE" button to complete the changes.
					</p>
					<h3 id="delete-themes">Delete Themes</h3>
					<p>
						Visit the{" "}
						<Link
							to={`/${process.env.REACT_APP_ADMIN_URL}/themes`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Themes
						</Link>{" "}
						page to view all of the themes. Select the checkbox next to the
						theme(s) you would like to remove and click the trash can icon.
					</p>

					{/* end of content */}

					<div className={classes.empty} />
					{"ヽ(*⌒▽⌒*)ﾉ"}
				</div>

				<Paper className={classes.sticky}>
					<Headings headings={nestedHeadings} activeId={activeId} />
				</Paper>
			</div>
		</>
	);
}
